import * as R from 'ramda';
import { isNilOrEmpty, sortByDateProp, updateIn, assoc } from '../../language/module';
import { toServerLocalTime, toTwoDigitYearRanges, UNIX_EPOCH_STR } from '../../language/time';
import { Resort } from '../../resortDetails/resort';
import { GuestDetails } from '../guestDetails';
import { AGE_BIN, DESCRIPTION, EFFECTIVE_DATE, EXPIRATION_DATE, SEASON } from './knownEntitlementAttributes';
import { BaseEntitlement, Entitlement, EntitlementAttr, EntitlementHotlist, QuickEntitlement } from './models';
import { HistoricalRedemption } from './redemption/historicalRedemption';
import { REDEMPTION_TYPE_ID } from './redemption/redemptionTypeIds';
import { ENTITLEMENT_CATEGORY_ID, ENTITLEMENT_STATUS, RELEVANT_STATUSES } from './constants';
import { IAppState } from '../../core/redux/IAppState';
import { getGlobalAndLocalResortList } from '../../resortDetails/module';
import { filterHotlistsByResortList } from '../hotlist/module';
import ImmutableMap from '../../language/ImmutableMap';
import { EntitlementType } from '../../entitlementTypes/entitlementType';


/**
 * Description attribute for Ikon Passes as found on a guest's entitlements
 */
export const IKON_PASS_DESC = 'Ikon Pass';

/**
 * Description attribute for Ikon Base Passes as found on a guest's entitlements
 */
export const IKON_BASE_PASS_DESC = 'Ikon Base Pass';

/**
 * Days/Code used by IKON to denote Unlimited Days for entitlements
 */
export const UNLIMITED_DAYS = 9999;


/**
 * Given a maybe guest, returns back that guest's age group if the maybe guest maybe has some entitlements. If no guest
 * or entitlements are found, then you get back a nice and fancy `undefined`
 */
export const getAgeGroup = (
  passEntitlementId: number,
  guest: GuestDetails | undefined,
): string | undefined => attrValueFor(
  AGE_BIN,
  R.find(entitlement => !!attrValueFor(AGE_BIN, entitlement), getActivePassEntitlements(passEntitlementId, guest)),
);


/**
 * Gets the guest (or lack thereof)'s entitlements. If no guest, no entitlements -- ya get undefined.
 */
export const getEntitlements = (guest: GuestDetails | undefined): QuickEntitlement[] | undefined => guest?.entitlements;


/**
 * Given some entitlements (or lack thereof), returns out the attributes off the entitlements. Not particularly
 * necessary, but much easier to figure out than `entitlementTypeAttributes` which is awful to type, and not
 * null-friendly. No entitlements, no attrs -- pretty straight-forward.
 */
export const getEntitlementAttrs = (
  entitlements: QuickEntitlement | Entitlement | undefined,
): EntitlementAttr[] | undefined => R.path(['entitlementTypeAttributes'], entitlements)
  || R.path(['entitlementAttributes'], entitlements);


/**
 * Looks up the value for the given key in the given maybe entitlements. If your entitlements is undefined, you'll get
 * back an undefined. Similarly, if the given attribute doesn't exist, you'll get back an undefined.
 */
export const attrValueFor = (
  attrKey: string,
  entitlement: QuickEntitlement | Entitlement | undefined,
): string | undefined => R.find(
  (attr: EntitlementAttr) => attr.name === attrKey, getEntitlementAttrs(entitlement) || [],
)?.value;


/**
 * Takes the given attribute off of each of the given entitlements, sorts them all by value ascending, then returns the
 * last element AKA the maximum. Returns the value from the attribute that was maximum.
 * If your entitlement is undefined, you'll get back an undefined.
 * Similarly, if the given attribute doesn't exist, you'll get back an undefined.
 */
export const maxAttrValueFor = (
  attrKey: string,
  entitlement: QuickEntitlement | Entitlement | undefined,
): string | undefined => R.last(
  (getEntitlementAttrs(entitlement) || [])
    .filter(attr => attr.name === attrKey && !isNilOrEmpty(attr.value))
    .map(attr => attr.value)
    .sort(),
);


/**
 * Given an QuickEntitlement we return true if the entitlement is deferred
 * @param entitlement
 */
export const isDeferred = (entitlement: BaseEntitlement): boolean =>
  entitlement.entitlementStatusId === ENTITLEMENT_STATUS.DEFERRED;


/**
 * Returns whether or not the given maybe entitlement is an Ikon Pass or Ikon Base Pass
 * Entitlement is undefined? You get back false.
 */
export const isIkonPass = (
  entitlement: QuickEntitlement | Entitlement | undefined,
): boolean => [IKON_BASE_PASS_DESC, IKON_PASS_DESC].includes(attrValueFor(DESCRIPTION, entitlement) || '');


/**
 * Returns whether or not the given maybe entitlement is for a given season.
 * Season is a string formatted as, e.g., "2020-2021"
 */
export const isSeason = (
  season: string,
  entitlement: QuickEntitlement | Entitlement | undefined,
): boolean => attrValueFor(SEASON, entitlement) === season;


/**
 * Curried predicate that returns whether or not the given maybe entitlement is for a given season.
 * Season is a string formatted as, e.g., "2020-2021"
 * Entitlement is undefined? You get back false.
 */
export const isIkonPassForSeason = (
  season: string,
) => (
  entitlement: QuickEntitlement | Entitlement | undefined,
): boolean => isSeason(season, entitlement) && isIkonPass(entitlement);


/**
 * Populates entitlements with their expected name and expiration date properties.
 * @param entitlements The entitlements to be populated
 */
export const normalizeEntitlements = (
  entitlements: Entitlement[] | undefined,
): Entitlement[] => (entitlements || []).map(entitlement => R.mergeDeepRight(entitlement, {
  expirationDate: getValidDate(entitlement.expirationDate) ?? new Date(maxAttrValueFor(EXPIRATION_DATE, entitlement) + 'T00:00:00' || UNIX_EPOCH_STR),
  effectiveDate: getValidDate(entitlement.effectiveDate) ?? new Date(maxAttrValueFor(EFFECTIVE_DATE, entitlement) + 'T00:00:00' || UNIX_EPOCH_STR),
}));


/**
 * Parses a value into a valid Date object if possible.
 * Returns undefined if the value is invalid, empty, or not a date.
 */
export const getValidDate = (value: unknown): Date | undefined => {
  if (value instanceof Date && !isNaN(value.getTime())) return value;

  if (typeof value === 'string' && value.trim()) {
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  }

  return undefined;
};


/**
 * Sorts the passed-in entitlements in order of ascending expiration date.
 */
export const sortByExpirationDateAsc = (
  entitlements: Array<QuickEntitlement | Entitlement>,
) => sortByDateProp('expirationDate', true, entitlements);


/**
 * Returns the expiration date of the given entitlement or Unix Epoch if none is found.
 */
export const getExpirationDate = (
  entitlement: Entitlement | QuickEntitlement | undefined,
): Date => new Date(entitlement?.expirationDate || attrValueFor(EXPIRATION_DATE, entitlement) || UNIX_EPOCH_STR);

/**
 * Returns the effective date of the given entitlement or Unix Epoch if none is found.
 */
export const getEffectiveDate = (
  entitlement: Entitlement | QuickEntitlement | undefined,
): Date => new Date(entitlement?.effectiveDate || attrValueFor(EFFECTIVE_DATE, entitlement) || UNIX_EPOCH_STR);


/**
 * Returns if an entitlement is relevant to the app
 */
const isRelevant = entitlement => RELEVANT_STATUSES.includes(entitlement.entitlementStatusId);


/**
 * Evaluates the expiration status of the entitlement passed in.
 * @param entitlement The entitlement to be checked for expiration status.
 * @param date The date against which to verify the expiration status of the entitlement.
 *             Defaults to today's date.
 * @returns true if the entitlement passed in is in date, otherwise false.
 */
export const isActive = (
  entitlement: Entitlement | QuickEntitlement,
  date: Date = new Date(),
): boolean => isRelevant(entitlement) && (getExpirationDate(entitlement) >= date);


/**
 * Evaluates the effective date of the entitlement passed in.
 * @param entitlement The entitlement to be checked for effective status.
 * @param date The date against which to verify the effective status of the entitlement.
 *             Defaults to today's date.
 * @returns true if the entitlement passed in less than or equal to date, otherwise false.
 */
export const isEffective = (
  entitlement: Entitlement | QuickEntitlement,
  date: Date = new Date(),
): boolean => getEffectiveDate(entitlement) <= date;   



/**
 * Evaluates the expiration status of the entitlement passed in.
 * @param entitlement The entitlement to be checked for FnF shared voucher status.
 * @param date The date against which to verify the expiration status of the entitlement.
 * Defaults to today's date.
 * @returns true if the entitlement is a FnF shared voucher.
 */
export const isSharedVoucher = (
  entitlement: Entitlement | QuickEntitlement,
  date: Date = new Date(),
): boolean =>
  entitlement.entitlementCategoryId
  === ENTITLEMENT_CATEGORY_ID.FNF_SHARED_VOUCHER
  && getExpirationDate(entitlement)
  > date;

/**
 * Determines if the guest has any fnf shared voucher entitlements
 * @param guest the guest details to evaluate
 * @returns true if the guest has any fnf shared voucher entitlements
 */
export const guestHasSharedVoucher = (guest?: GuestDetails): boolean => {
 return !!(
    guest &&
    (guest.entitlements || []).some(ent => isSharedVoucher(ent) && (
      ent.entitlementStatusId === ENTITLEMENT_STATUS.HOTLIST 
      || ent.entitlementStatusId === ENTITLEMENT_STATUS.ENABLED
      || ent.entitlementStatusId === ENTITLEMENT_STATUS.SHARED))
  );
}


/**
 * For the maybe guest and their maybe entitlements, filters down to all of the pass entitlements (which will have a
 * Description key in their attrs for the pass name) and returns the results. If any of the maybes don't resolve, returns
 * an empty array.
 */
export const getPassEntitlements = (
  passEntitlementId: number,
  guest: GuestDetails | undefined,
  sortFn: ((arr: QuickEntitlement[]) => QuickEntitlement[]) = sortByExpirationDateAsc,
): QuickEntitlement[] => {
  const entitlements = (getEntitlements(guest) || [])
    .filter(entitlement => isRelevant(entitlement) && entitlement.entitlementCategoryId === passEntitlementId);

  return sortFn(entitlements);
};

/**
 * Returns all pass entitlements regardless of status.
 */
export const getExpiredPassEntitlementsWithAnyEntitlementStatus = (
  passEntitlementId: number,
  guest: GuestDetails | undefined,
  sortFn: ((arr: QuickEntitlement[]) => QuickEntitlement[]) = sortByExpirationDateAsc,
  date: Date = new Date(),
): QuickEntitlement[] => {
  const entitlements = (getEntitlements(guest) || [])
    .filter(entitlement => entitlement.entitlementCategoryId === passEntitlementId && (getExpirationDate(entitlement) < date));
  return sortFn(entitlements);
};

/**
 * For the maybe guest and their maybe entitlements, filters down to all of the pass entitlements (which will have a
 * Description key in their attrs for the pass name) and returns the results. Filter out any expired or disabled
 * passes (but include hotlisted passes).  If any of the maybes don't resolve, returns an empty array.
 */
export const getActivePassEntitlements = (
  passEntitlementId: number,
  guest: GuestDetails | undefined,
): QuickEntitlement[] => (getPassEntitlements(passEntitlementId, guest) || []).filter(ent => isActive(ent));

/**
 * For the maybe guest and their maybe entitlements, filters down to all of the expired or disabled pass entitlements (which will have a
 * Description key in their attrs for the pass name) and returns the results. Filter out any active
 * passes.  If any of the maybes don't resolve, returns an empty array.
 */
export const getExpiredPassEntitlements = (
  passEntitlementId: number,
  guest: GuestDetails | undefined,
): QuickEntitlement[] => getExpiredPassEntitlementsWithAnyEntitlementStatus(passEntitlementId, guest) || [];


/**
 * Gets the guest (or lack thereof)'s preferred pass and returns it's id and name. If no guest, no preferred pass
 */
export const getPreferredPassInfo = (
  passEntitlementId: number,
  guest: GuestDetails | undefined,
  allEntitlementTypes: ImmutableMap<EntitlementType>,
): QuickEntitlement | undefined => {
  const pass = getPreferredPass(passEntitlementId, guest);
  return pass && assoc('name', nameWithSeason(pass, allEntitlementTypes), pass);
};


/**
 * Gets the guest (or lack thereof)'s most active pass based on number of redemptions
 */
export const getMostActivePass = (
  guest: GuestDetails | undefined,
  passes: QuickEntitlement[],
): QuickEntitlement | undefined => {

  if (!passes.length) {
    return undefined;
  }


  interface Data {
    entitlementId: string | number;
    daysUsed: number;
  }


  // creates an array of objects with aggregated relevant dtw and fnf data
  const data: Data[] = passes.map(pass => ({
    entitlementId: pass.entitlementId,
    daysUsed:
                   ((guest?.directToWindow || []).find(dtw => dtw.entitlementId === pass.entitlementId)?.used || 0) +
                     ((guest?.friendsAndFamily || []).find(fnf => fnf.entitlementId === pass.entitlementId)?.used || 0),
  }));

  const mostActiveEntitlementId =
          data.reduce((prev, next) => prev.daysUsed > next.daysUsed ? prev : next).entitlementId;

  return passes.find(pass => pass.entitlementId === mostActiveEntitlementId);

};


/**
 * Gets the highest ranking pass on the guest if the guest exists and their entitlements are loaded. If the maybes fail,
 * you get an undefined. Current ranking (most to least important): Ikon Pass, Ikon Base Pass, Other resort passes
 */
export const getPreferredPass = (
  passEntitlementId: number,
  guest: GuestDetails | undefined,
): QuickEntitlement | undefined => {
  const passes        = getActivePassEntitlements(passEntitlementId, guest);
  const currentPasses = passes.filter(entitlement => isActive(entitlement));

  // Prefer Ikon Pass for its better entitlements
  const currentIkonPass = R.find(
    (entitlement: QuickEntitlement) => attrValueFor(DESCRIPTION, entitlement) === IKON_PASS_DESC,
    currentPasses,
  );

  if (currentIkonPass) {
    return currentIkonPass;
  }

  // Failing an Ikon Pass, do we have an Ikon Base Pass?
  const currentIkonBasePass = R.find(
    (entitlement: QuickEntitlement) => attrValueFor(DESCRIPTION, entitlement) === IKON_BASE_PASS_DESC,
    currentPasses,
  );

  if (currentIkonBasePass) {
    return currentIkonBasePass;
  }

  // Try finding a current local pass
  const currentLocalPass = sortByDateProp(EXPIRATION_DATE, false, currentPasses)[0];

  if (currentLocalPass) {
    return currentLocalPass;
  }

  // Send back first pass found
  return sortByDateProp(EXPIRATION_DATE, false, passes)[0];
};


/**
 * Returns whether or not your maybe guest has unexpired entitlements that are active.
 * TODO: Check the active flag when the entitlements API starts sending it
 */
export const hasActivePass = (
  passEntitlementId: number,
  guest: GuestDetails | undefined,
): boolean => getActivePassEntitlements(passEntitlementId, guest).length > 0;


/**
 * Given a maybe entitlement, returns the name of that entitlement with the season of that entitlement post-fixed. If no
 * entitlement is given, returns empty string.
 *
 * E.g. 19-20 Ikon Pass
 */
export const nameWithSeason =
  (entitlement: Entitlement | QuickEntitlement | undefined, allEntitlementTypes: ImmutableMap<EntitlementType>) => {
    const desc       = getDescription(entitlement, allEntitlementTypes) || '';
    const seasonAttr = attrValueFor(SEASON, entitlement) || '';

    if (R.isEmpty(seasonAttr)) {
      return desc;
    }

    const season = toTwoDigitYearRanges(seasonAttr);

    if (R.isEmpty(desc)) {
      return season;
    }

    return season + ' ' + desc;
  };


/**
 * Given a maybe guest, returns either their list of pass names (just the name of the pass e.g. Ikon Base Pass) or undefined
 * for the case that there is no guest, or an empty array if there are no guest
 * entitlements on the given guest.
 */
export const getPassNames = (
  passEntitlementId: number,
  guest: GuestDetails | undefined,
): string[] => R.map(
  entitlement => attrValueFor(DESCRIPTION, entitlement) || '',
  getActivePassEntitlements(passEntitlementId, guest),
);


/**
 * Gets the guest (or lack thereof)'s entitlements and returns it with a friendly name for humans. For our purposes,
 * entitlements are functioning as passes, with their descriptions being the pass names. If no guest, no entitlements
 */
export const getPassesWithSeason = (
  passEntitlementId: number,
  guest: GuestDetails | undefined,
  allEntitlementTypes: ImmutableMap<EntitlementType>,
): QuickEntitlement[] | undefined => R.map(
  entitlement => assoc('name', nameWithSeason(entitlement, allEntitlementTypes), entitlement),
  getPassEntitlements(passEntitlementId, guest) || [],
);


/**
 * Gets the guest (or lack thereof)'s entitlements and returns it with a friendly name for humans. For our purposes,
 * entitlements are functioning as passes, with their descriptions being the pass names. If no guest, empty an empty
 * array is returned.
 */
export const getActivePassesWithSeason = (
  passEntitlementId: number,
  guest: GuestDetails | undefined,
  allEntitlementTypes: ImmutableMap<EntitlementType>,
): QuickEntitlement[] =>
  R.filter((ent) =>
    isActive(ent), getPassesWithSeason(passEntitlementId, guest, allEntitlementTypes) || []);


/**
 * Takes an entitlement redemption and a guest, and adds a dummy historical redemption while updating the guest's
 * entitlements so that the redemption is reflected in their numbers. Does not like it when you pass undefined -- by the
 * time you've redeemed an entitlement, you should know you have a guest etc.
 * @param guest - The guest whose entitlement is being redeemed
 * @param entitlementId - The ID of the entitlement that we're redeeming
 * @param quantityRedeemed - The number of entitlements being redeemed (we don't validate this number -- negatives possible)
 * @param entitlementType - The name of property for the entitlement group on guestDetails.
 * @param redemptionDate - The date of the entitlement redemption.
 * @param homeResortName - The name of the current resort where the user is using Resort Tools.
 * @param redemptionIdReference - This references the original redemption tied to a negative corrective redemption.
 * @param modifiedById - The name of the current Resort Tools user.
 */
export const addRedeemedEntitlement = (
  guest: GuestDetails,
  entitlementId: number,
  quantityRedeemed: number,
  entitlementType: 'friendsAndFamily' | 'directToWindow',
  redemptionDate: Date,
  homeResortName: string,
  redemptionIdReference: string,
  modifiedById: string,
): GuestDetails => {
  // Grab the old entitlement that we want to update
  const oldEntitlement = R.find(
    entitlement => entitlement.entitlementId === entitlementId,
    guest[entitlementType] || [],
  );

  // Can't update a non-existent entitlement
  if (!oldEntitlement) {
    return guest;
  }

  // Make a new historical redemption with date, location, quantity, resort, id, and type reflecting the redemption
  const newRedemptionHistory = {
    redeemDate:             toServerLocalTime(redemptionDate),
    redeemLocation:         'ResortTools',
    redeemQuantity:         quantityRedeemed,
    redeemResort:           homeResortName,
    redemptionExternalCode: undefined,
    redemptionID:           entitlementId,
    redemptionTypeId:       REDEMPTION_TYPE_ID.RESORT_TOOLS,
    redemptionIdReference,
    modifiedById:           modifiedById,
  } as HistoricalRedemption;

  // Make a new, updated entitlement with the available/used numbers and redemptionHistory appropriately updated
  // 1/7/2019 - Had to modify to make sure it does not reduce available days if UNLIMITED_DAYS (see const at top)
  const newEntitlement = ({
    ...oldEntitlement,
    used:              (+oldEntitlement.used) + (+quantityRedeemed), // Make them numbers because sometimes they're strings?
    available:         (+oldEntitlement.available) === UNLIMITED_DAYS
                         ? UNLIMITED_DAYS
                         : (+oldEntitlement.available) - (+quantityRedeemed),
    redemptionHistory: R.concat(oldEntitlement.redemptionHistory || [], [newRedemptionHistory]),
  });

  // Mash the new entitlement obj back onto the guest while removing the old entitlement obj
  return assoc(
    entitlementType,
    updateIn<Entitlement>(
      () => newEntitlement,
      entitlement => entitlement.entitlementId === newEntitlement.entitlementId,
      guest[entitlementType] || [],
    ),
    guest,
  );
};


/**
 * Returns the types of all active entitlements for the given guest (or an empty array if the guest has none).
 * This can be useful e.g. for retrieving the required waiver types associated with the guest's entitlements.
 * @param guest the guest whose entitlement types we wish to retrieve
 * @param entitlementTypes the list of entitlement type objects with which guest entitlements can be associated
 * (this should be a list of all such types)
 */
export const getActiveEntitlementTypes = (
  guest: GuestDetails | undefined,
  entitlementTypes: ImmutableMap<EntitlementType> | undefined,
): EntitlementType[] => {
  if (!guest?.entitlements || !entitlementTypes) {
    return [];
  }

  const activeEntitlements = R.filter(entitlement => isActive(entitlement, new Date()), guest!.entitlements!);

  return R.uniq(activeEntitlements.map(entitlement => entitlementTypes!.get(`${entitlement.entitlementTypeId}`)));
};


/**
 * Returns the types of all active entitlements effective as of today for the given guest (or an empty array if the guest has none).
 * This can be useful e.g. for retrieving the required waiver types associated with the guest's entitlements.
 * @param guest the guest whose entitlement types we wish to retrieve
 * @param entitlementTypes the list of entitlement type objects with which guest entitlements can be associated
 * (this should be a list of all such types)
 */
export const getActiveEntitlementTypesEffectiveToday = (
  guest: GuestDetails | undefined,
  entitlementTypes: ImmutableMap<EntitlementType> | undefined,
): EntitlementType[] => {
  if (!guest?.entitlements || !entitlementTypes) {
    return [];
  }

  const effectiveEntitlements = R.filter(entitlement => isActive(entitlement, new Date()) && isEffective(entitlement, new Date()), guest!.entitlements!);

  return R.uniq(effectiveEntitlements.map(entitlement => entitlementTypes!.get(`${entitlement.entitlementTypeId}`)));
};


/**
 * Returns a maybe entitlement type for a maybe entitlement and the maybe list of all entitlement types
 */
export const typeFor = (
  entitlement: BaseEntitlement | undefined,
  entitlementTypes: ImmutableMap<EntitlementType> | undefined,
): EntitlementType | undefined => entitlementTypes?.get(`${entitlement?.entitlementTypeId}`);


/**
 * Generate a title for an entitlement hotlist which is not provided via the API.
 * Returns a new hotlist entry with the title populated
 * @param hotlist The hotlist entry to add a title to
 * @param entitlement Entitlement used to generate the title text
 * @param resortList List of resorts used to map the hotlist resort abbreviation to a long resort name
 */
export const setEntitlementHotlistTitle = (
  hotlist: EntitlementHotlist,
  entitlement: QuickEntitlement,
  resortList: Resort[] | undefined,
): EntitlementHotlist => assoc(
  'title',
  `${entitlement.name} - `
  + (
    hotlist.isHotlistGlobal
      ? 'All Resorts'
      : R.find(res => res.resortAbbreviation === hotlist.resortAbbreviation, resortList || [])?.resortName
      || hotlist.resortAbbreviation
  ),
  hotlist,
);


/**
 * Convenience function to return an array of entitlement IDs for a guest
 *
 * @param guest - The guest whose entitlements you want
 */
export const getGuestEntitlementIds = (
  guest?: GuestDetails,
): number[] => R.map(ent => ent.entitlementId, guest?.entitlements || []);

const getEntitlementType =
        (entitlement: Entitlement | QuickEntitlement | undefined, allTypes: ImmutableMap<EntitlementType> | undefined):
          EntitlementType | undefined => {

          if (isNilOrEmpty(allTypes) || isNilOrEmpty(entitlement)) {
            return undefined;
          }

          const ourType = allTypes!
            .getValues()!
            .find(type => type.entitlementTypeId === entitlement?.entitlementTypeId);

          return ourType ?? undefined;
        };

export const getDescription = (
  entitlement: Entitlement | QuickEntitlement | undefined,
  allEntitlementTypes: ImmutableMap<EntitlementType> | undefined,
): string => R.path(['publicDescription'], getEntitlementType(entitlement, allEntitlementTypes)) || '';

export const getSeason = (
  entitlement: Entitlement | QuickEntitlement | undefined,
  allEntitlementTypes: ImmutableMap<EntitlementType> | undefined,
): string => R.path(['seasonDescription'], getEntitlementType(entitlement, allEntitlementTypes)) || '';

export const getGuestHotlists = (state: IAppState): ImmutableMap<EntitlementHotlist[]> => {
  const allHotlists = state.entitlementHotlists;
  const resortList  = getGlobalAndLocalResortList(state);
  return filterHotlistsByResortList(allHotlists, resortList || []);
};
